import React, { useState, useEffect, useCallback } from 'react';
import MDEditor from '@uiw/react-md-editor';
import NavigationMinimal from '../components/layout/NavigationMinimal';
import { LoadingSpinner } from '../components/common';
import AktuellesFotos from '../components/aktuelles/AktuellesFotos';
import { fetchAktuellesPosts } from '../services/aktuellesService';

const AktuellesPage = () => {
    const [posts, setPosts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const limit = 10;

    const loadPosts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchAktuellesPosts({ limit, page });
            setPosts(result.posts || []);
            setTotal(result.total || 0);
        } catch {
            setError('Beiträge konnten nicht geladen werden. Bitte versuche es später erneut.');
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => { loadPosts(); }, [loadPosts]);

    const totalPages = Math.ceil(total / limit);

    return (
        <>
            <NavigationMinimal />
            <div className="sub-hero">
                <span className="sub-hero__eyebrow">Neuigkeiten</span>
                <h1 className="sub-hero__title" data-testid="aktuelles-heading">Aktuelles</h1>
                <p className="sub-hero__lead">Turnierberichte, Trainingstipps und Community-News aus dem KickerEck.</p>
            </div>
            <main className="page-shell page-shell--after-hero aktuelles-page">

                {loading && (
                    <div className="aktuelles-state-box">
                        <LoadingSpinner />
                    </div>
                )}

                {!loading && error && (
                    <div className="aktuelles-error-box" role="alert" data-testid="aktuelles-error">
                        <span className="aktuelles-state-icon">⚠</span>
                        <p>{error}</p>
                        <button className="aktuelles-retry-btn" onClick={loadPosts}>Erneut versuchen</button>
                    </div>
                )}

                {!loading && !error && posts.length === 0 && (
                    <div className="aktuelles-empty-box" data-testid="aktuelles-empty">
                        <span className="aktuelles-state-icon">📰</span>
                        <p>Keine Beiträge vorhanden.</p>
                    </div>
                )}

                {!loading && !error && posts.map((post) => {
                    const dateLabel = post.datum ? new Date(post.datum).toLocaleDateString('de-DE', {
                        year: 'numeric', month: 'long', day: 'numeric'
                    }) : null;

                    return (
                        <article key={post.id} className="aktuelles-post-card" data-testid={`aktuelles-post-${post.id}`}>
                            <div className="aktuelles-post-body">
                                {dateLabel && <span className="aktuelles-post-date">{dateLabel}</span>}
                                <h2 className="aktuelles-post-title">{post.titel}</h2>
                                <div className="aktuelles-post-content" data-color-mode="light">
                                    <MDEditor.Markdown source={post.text || ''} />
                                </div>
                                {post.fotos && post.fotos.length > 0 && (
                                    <AktuellesFotos photos={post.fotos} />
                                )}
                            </div>
                        </article>
                    );
                })}

                {totalPages > 1 && (
                    <nav className="aktuelles-pagination" aria-label="Seitennavigation" data-testid="aktuelles-pagination">
                        <button
                            className={`aktuelles-page-btn${page <= 1 ? ' disabled' : ''}`}
                            onClick={() => setPage(Math.max(1, page - 1))}
                            disabled={page <= 1}
                        >‹</button>
                        {Array.from({ length: totalPages }, (_, i) => (
                            <button
                                key={i}
                                className={`aktuelles-page-btn${page === i + 1 ? ' active' : ''}`}
                                onClick={() => setPage(i + 1)}
                                data-testid={`aktuelles-page-${i + 1}`}
                            >{i + 1}</button>
                        ))}
                        <button
                            className={`aktuelles-page-btn${page >= totalPages ? ' disabled' : ''}`}
                            onClick={() => setPage(Math.min(totalPages, page + 1))}
                            disabled={page >= totalPages}
                        >›</button>
                    </nav>
                )}
            </main>
        </>
    );
};

export default AktuellesPage;
